/** Members, roles, per-member permission overrides and access groups. */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Lock, Plus, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PERMISSION_LABELS, PERMISSIONS, type Permission, type Role } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useAccessGroups, useMe, useMembers, useMembersPage, useRoles, type AccessGroupDTO, type MemberDTO, type MembersParams } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { MultiSelect } from '../../components/MultiSelect';
import { TableScroll } from '../../components/TableScroll';
import { LoadMore } from '../../components/LoadMore';
import { useDebounced } from '../../lib/useDebounced';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { copy } from '../../lib/clipboard';
import { RolesAdmin } from './RolesAdmin';
import { SignedInAdmin } from './SignedInAdmin';
import { MfaPolicyAdmin } from './MfaPolicyAdmin';
import { SsoAdmin } from './SsoAdmin';
// 1.6.0 (T, access/AssistantsAdmin.tsx): AI assistants.
import { AssistantsAdmin } from './access/AssistantsAdmin';
import { queryKeys } from '../../api/queryKeys';

export function AccessAdmin() {
  const me = useMe().data!;
  const isAdmin = me.permissions.includes('admin');
  const canGroups = me.permissions.includes('access.manage');
  return (
    <div className="page">
      <h2>Access</h2>
      <p className="lede">Roles set a starting point; administrators can grant or remove individual permissions per member. Access groups restrict individual assets to the people who need them. Every change applies on the member’s next request and is recorded in the audit log.</p>
      {isAdmin ? <><Members /><SignedInAdmin /><MfaPolicyAdmin /><SsoAdmin /><AssistantsAdmin /><RolesAdmin /></> : (
        <div className="panel-card">
          <h3><ShieldCheck size={16} color="var(--accent-strong)" /> Your access</h3>
          <p className="muted">{me.user.displayName} · {me.roleLabel}</p>
          <div className="tags">{me.permissions.map((p) => <span key={p} className="tag">{PERMISSION_LABELS[p].label}</span>)}</div>
        </div>
      )}
      {canGroups && <Groups />}
    </div>
  );
}

function patchMember(id: string, body: Record<string, unknown>) {
  return api(`/api/v1/members/${id}`, { method: 'PATCH', json: body });
}

function Members() {
  const me = useMe().data!;
  // 1.2.1: search, role, group and status filters, kept while this tab is open; 50 at a time.
  const [text, setText] = useState('');
  const [role, setRole] = useState('');
  const [groupFilter, setGroupFilter] = useState<Set<string>>(() => new Set());
  const [status, setStatus] = useState<MembersParams['status']>('all');
  const search = useDebounced(text.trim(), 250);
  const q = useMembersPage({ q: search, role, groups: [...groupFilter], status });
  const roles = useRoles().data?.items ?? [];
  const groups = useAccessGroups().data?.items ?? [];
  const filtered = Boolean(search || role || groupFilter.size || status !== 'all');
  const total = q.data?.total;
  const qc = useQueryClient();
  const [editing, setEditing] = useState<MemberDTO | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<MemberDTO | null>(null);
  const [reset, setReset] = useState<{ member: MemberDTO; password: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.members() });
  const change = async (m: MemberDTO, body: Record<string, unknown>) => {
    try {
      await patchMember(m.id, body);
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const resetPassword = async (m: MemberDTO) => {
    if (!window.confirm(`Reset the password of ${m.displayName}? They are signed out everywhere and must choose a new password when they next sign in with the temporary one.`)) return;
    try {
      const r = await api<{ temporaryPassword: string }>(`/api/v1/members/${m.id}/password-reset`, { method: 'POST', json: {} });
      setReset({ member: m, password: r.temporaryPassword });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const resetMfa = async (m: MemberDTO) => {
    if (!window.confirm(`Reset two-step sign-in for ${m.displayName}? Their authenticator and recovery codes stop working and they are signed out; if a policy requires it, they set it up again at their next sign-in.`)) return;
    try {
      await api(`/api/v1/members/${m.id}/mfa-reset`, { method: 'POST', json: {} });
      toast(`Two-step sign-in reset for ${m.displayName}`);
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  return (
    <section className="panel-card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ flex: 1, margin: 0 }}><Users size={16} color="var(--accent-strong)" /> Members</h3>
        <button className="btn sm" onClick={() => setAdding(true)} data-testid="add-member"><UserPlus size={14} /> Add member</button>
      </div>
      <div className="members-toolbar" data-testid="members-filters">
        <input className="input" type="search" placeholder="Search name or email" aria-label="Search members" value={text} onChange={(e) => setText(e.target.value)} data-testid="members-search" />
        <select className="select" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} data-testid="members-role">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        <MultiSelect label="Groups" testId="members-groups" selected={groupFilter} onChange={setGroupFilter}
          options={[{ value: 'none', label: 'No group' }, ...groups.map((g) => ({ value: g.id, label: g.name }))]}
          summary={groupFilter.size === 0 ? 'All groups' : groupFilter.size === 1 ? ([...groupFilter][0] === 'none' ? 'No group' : groups.find((g) => g.id === [...groupFilter][0])?.name ?? '1 group') : `${groupFilter.size} groups`} />
        <select className="select" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as MembersParams['status'])} data-testid="members-status">
          <option value="all">Active and disabled</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        {filtered && <button className="btn ghost sm" onClick={() => { setText(''); setRole(''); setGroupFilter(new Set()); setStatus('all'); }}>Clear filters</button>}
        <span className="faint" style={{ marginLeft: 'auto', fontSize: 12 }} data-testid="members-count">{total == null ? '' : `${total.toLocaleString()} ${total === 1 ? 'person' : 'people'}`}</span>
      </div>
      <TableScroll>
      <table className="table compact" data-testid="members">
        <thead><tr><th>Name</th><th>Role</th><th>Adjustments</th><th>Groups</th><th>Sign-in</th><th>Last sign-in</th><th /></tr></thead>
        <tbody>
          {q.data && q.data.items.length === 0 && <tr><td colSpan={7} className="muted">{filtered ? 'No members match these filters.' : 'No members yet.'}</td></tr>}
          {(q.data?.items ?? []).map((m) => (
            <tr key={m.id} className={m.disabled ? 'faint' : undefined} data-member={m.email}>
              <td className="wrap" style={{ minWidth: 170 }}><div style={{ fontWeight: 560 }}>{m.displayName}</div><div className="faint break-any">{m.email}</div></td>
              <td className="nowrap">
                <select className="select" style={{ minWidth: 136 }} aria-label={`Role for ${m.email}`} value={m.role} disabled={m.disabled} onChange={(e) => void change(m, { role: e.target.value })}>
                  {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </td>
              <td>
                <button className="btn ghost sm" onClick={() => setEditing(m)} data-testid="edit-permissions">
                  {m.grants.length || m.revokes.length ? `${m.grants.length ? `+${m.grants.length}` : ''}${m.grants.length && m.revokes.length ? ' ' : ''}${m.revokes.length ? `−${m.revokes.length}` : ''}` : 'Role defaults'}
                </button>
              </td>
              <td className="muted wrap" style={{ minWidth: 100 }}>{m.groups.map((g) => groups.find((x) => x.id === g)?.name).filter(Boolean).join(', ') || '—'}</td>
              <td className="muted" data-testid="member-signin">
                {m.sso && <span className="pill" title="Signs in through an identity provider">SSO</span>} {m.mfa && <span className="pill accent" title="Two-step sign-in is set up">2-step</span>}
                {!m.sso && !m.mfa && '—'}
              </td>
              <td className="muted nowrap">{m.lastSignInAt ? relative(m.lastSignInAt) : 'Never'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'normal', minWidth: 150 }}>
                {m.id !== me.user.id && (
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {!m.disabled && m.hasPassword !== false && <button className="btn ghost sm" onClick={() => void resetPassword(m)} data-testid="reset-password">Reset password</button>}
                    {m.mfa && <button className="btn ghost sm" onClick={() => void resetMfa(m)} data-testid="reset-mfa">Reset two-step</button>}
                    <a className="btn ghost sm" href={`/api/v1/members/${m.id}/personal-data`} download title="Everything PRISM keeps about this person, as JSON (GDPR)" data-testid="export-personal-data">Export data</a>
                    <button className="btn ghost sm" onClick={() => void change(m, { disabled: !m.disabled })}>{m.disabled ? 'Enable' : 'Disable'}</button>
                    <button className="btn ghost sm" onClick={() => setDeleting(m)} data-testid="delete-member">Delete…</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
      <LoadMore list={q} shown={q.data?.items.length} noun="people" />
      {editing && <PermissionsModal member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); }} />}
      {reset && (
        <Modal title="Password reset" onClose={() => setReset(null)} actions={<button className="btn primary" onClick={() => setReset(null)}>Done</button>}>
          <p className="muted" style={{ marginTop: 0 }}>Give this temporary password to {reset.member.displayName} ({reset.member.email}) through a secure channel. It is not shown again. They choose their own password when they sign in with it.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <code className="code-block" style={{ flex: 1 }} data-testid="temporary-password">{reset.password}</code>
            <button className="btn icon" aria-label="Copy password" onClick={() => copy(reset.password)}><Copy size={14} /></button>
          </div>
        </Modal>
      )}
      {deleting && <DeleteMemberModal member={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); void refresh(); void qc.invalidateQueries({ queryKey: queryKeys.accessGroups() }); }} />}
      {adding && <AddMemberModal onClose={() => setAdding(false)} onAdded={() => void refresh()} />}
    </section>
  );
}

interface DeleteCounts { collections: number; sharedCollections: number; openTasks: number; apiKeysCreated: number }
interface DeletePreview {
  email: string; workspaces: number; adminEverywhere: boolean; refusal: 'SELF_DELETE' | 'LAST_ADMIN' | null;
  canDeleteAccount: boolean; canRemoveFromWorkspace: boolean; recreatedAtStartup: boolean; account: DeleteCounts; workspace: DeleteCounts;
}

/** Delete a person (closing the account) or, when it is not yours to close, remove them from this workspace. */
function DeleteMemberModal({ member, onClose, onDone }: { member: MemberDTO; onClose: () => void; onDone: () => void }) {
  const preview = useQuery({ queryKey: ['member-delete-preview', member.id], queryFn: () => api<DeletePreview>(`/api/v1/members/${member.id}/delete-preview`), gcTime: 0 });
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const p = preview.data;
  const wholeAccount = !!p?.canDeleteAccount;
  const counts = p ? (wholeAccount ? p.account : p.workspace) : null;
  const confirmed = typed.trim().toLowerCase() === member.email.toLowerCase();
  async function run() {
    setBusy(true);
    try {
      await api(`/api/v1/members/${member.id}${wholeAccount ? '' : '?scope=workspace'}`, { method: 'DELETE' });
      toast(wholeAccount ? `${member.displayName} deleted` : `${member.displayName} removed from this workspace`);
      onDone();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  const verb = wholeAccount ? 'Delete account' : 'Remove from this workspace';
  return (
    <Modal title={wholeAccount || !p ? `Delete ${member.displayName}` : `Remove ${member.displayName} from this workspace`} onClose={onClose}
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn danger" disabled={!p || !!p.refusal || !confirmed || busy} onClick={() => void run()} data-testid="confirm-delete-member">{verb}</button></>}>
      {preview.isError && <p className="muted" style={{ marginTop: 0 }}>{errorMessage(preview.error)}</p>}
      {!p && !preview.isError && <p className="muted" style={{ marginTop: 0 }}>Checking what this affects…</p>}
      {p && p.refusal === 'SELF_DELETE' && <p className="muted" style={{ marginTop: 0 }}>You cannot delete your own account.</p>}
      {p && p.refusal === 'LAST_ADMIN' && <p className="muted" style={{ marginTop: 0 }}>{member.displayName} is the only active administrator. Make someone else an administrator first.</p>}
      {p && !p.refusal && counts && (
        <>
          {wholeAccount ? (
            <ul data-testid="delete-effects" style={{ marginTop: 0 }}>
              <li>{member.displayName} ({member.email}) is signed out everywhere and can no longer sign in.</li>
              <li>Removed from {p.workspaces} {p.workspaces === 1 ? 'workspace' : 'workspaces'}.</li>
              <li>Their {counts.collections} {counts.collections === 1 ? 'collection is' : 'collections are'} deleted{counts.sharedCollections ? `, ${counts.sharedCollections} of them public or shared with others` : ''}. Assets are kept.</li>
              <li>{counts.openTasks} open {counts.openTasks === 1 ? 'task becomes' : 'tasks become'} unassigned.</li>
              {counts.apiKeysCreated > 0 && <li>{counts.apiKeysCreated} API {counts.apiKeysCreated === 1 ? 'key' : 'keys'} they created keep working.</li>}
              <li>History keeps their name, marked “(deleted)”.</li>
              <li>The email address can be invited again as a new person.</li>
            </ul>
          ) : (
            <>
              <p className="muted" style={{ marginTop: 0 }}>{member.displayName} also belongs to a workspace you do not administer, so the account itself stays. This removes them from this workspace only:</p>
              <ul data-testid="delete-effects">
                <li>Signed out of this workspace and removed from its groups.</li>
                <li>Their {counts.collections} {counts.collections === 1 ? 'collection' : 'collections'} here {counts.collections === 1 ? 'is' : 'are'} deleted{counts.sharedCollections ? `, ${counts.sharedCollections} of them public or shared` : ''}. Assets are kept.</li>
                <li>{counts.openTasks} open {counts.openTasks === 1 ? 'task becomes' : 'tasks become'} unassigned.</li>
                <li>History here keeps their name.</li>
              </ul>
            </>
          )}
          {/* 1.8.1: only the demo accounts are recreated at startup; the bootstrap administrator is created once. */}
          {wholeAccount && p.recreatedAtStartup && (
            <p className="muted" data-testid="delete-bootstrap-warning"><b>Note:</b> this is a demo account PRISM creates at startup while PRISM_SEED_DEMO_USERS is on. It is created again as a new account the next time PRISM starts, unless the setting is turned off.</p>
          )}
          <div className="field"><label htmlFor="del-confirm">Type {member.email} to confirm</label>
            <input id="del-confirm" className="input" autoFocus autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-email" />
          </div>
        </>
      )}
    </Modal>
  );
}

type Override = 'role' | 'grant' | 'revoke';

function PermissionsModal({ member, onClose, onSaved }: { member: MemberDTO; onClose: () => void; onSaved: () => void }) {
  const [state, setState] = useState<Record<Permission, Override>>(() => Object.fromEntries(PERMISSIONS.map((p) => [p, member.grants.includes(p) ? 'grant' : member.revokes.includes(p) ? 'revoke' : 'role'])) as Record<Permission, Override>);
  const role = useRoles().data?.items.find((r) => r.key === member.role);
  const fromRole = (p: Permission) => (role?.permissions ?? []).includes(p);
  async function save() {
    const grants = PERMISSIONS.filter((p) => state[p] === 'grant' && !fromRole(p));
    const revokes = PERMISSIONS.filter((p) => state[p] === 'revoke' && fromRole(p));
    try {
      await patchMember(member.id, { grants, revokes });
      toast('Permissions updated');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <Modal title={`Permissions for ${member.displayName}`} onClose={onClose} wide actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" onClick={() => void save()} data-testid="save-permissions">Save</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>Starting from the {role?.name ?? member.roleName ?? member.role} role. Choose Allow or Deny to override a single permission.</p>
      <div className="perm-grid">
        {PERMISSIONS.map((p) => {
          const locked = p === 'admin' && member.role === 'admin';
          const effective = state[p] === 'grant' || (state[p] === 'role' && fromRole(p));
          return (
            <div key={p} style={{ display: 'contents' }}>
              <div>
                <div style={{ fontWeight: 550, color: effective ? 'var(--text)' : 'var(--muted)' }}>{PERMISSION_LABELS[p].label}</div>
                <div className="help">{PERMISSION_LABELS[p].help}</div>
              </div>
              <div className="segmented" aria-label={PERMISSION_LABELS[p].label}>
                {(['role', 'grant', 'revoke'] as const).map((o) => (
                  <button key={o} type="button" aria-pressed={state[p] === o} disabled={locked && o === 'revoke'} onClick={() => setState({ ...state, [p]: o })} data-testid={`perm-${p}-${o}`}>
                    {o === 'role' ? `Role (${fromRole(p) ? 'yes' : 'no'})` : o === 'grant' ? 'Allow' : 'Deny'}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function AddMemberModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const roles = useRoles().data?.items ?? [];
  const [role, setRole] = useState<Role>('viewer');
  const [password, setPassword] = useState<string | null>(null);
  async function add() {
    try {
      const r = await api<{ id: string; initialPassword: string | null }>('/api/v1/members', { method: 'POST', json: { email, displayName: name || email, role } });
      onAdded();
      if (r.initialPassword) setPassword(r.initialPassword);
      else { toast('Existing account added to this workspace'); onClose(); }
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  if (password) {
    return (
      <Modal title="Member added" onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
        <p className="muted" style={{ marginTop: 0 }}>Share this one-time password with {email} through a secure channel. It is not shown again. They choose their own password when they first sign in.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <code className="code-block" style={{ flex: 1 }} data-testid="initial-password">{password}</code>
          <button className="btn icon" aria-label="Copy password" onClick={() => copy(password)}><Copy size={14} /></button>
        </div>
      </Modal>
    );
  }
  return (
    <Modal title="Add member" onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={!email.includes('@')} onClick={() => void add()} data-testid="confirm-add-member">Add</button></>}>
      <div className="field"><label htmlFor="m-email">Email</label><input id="m-email" className="input" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      <div className="field"><label htmlFor="m-name">Name</label><input id="m-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="field"><label htmlFor="m-role">Role</label>
        <select id="m-role" className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>{roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}</select>
      </div>
    </Modal>
  );
}

function Groups() {
  const me = useMe().data!;
  const q = useAccessGroups();
  const members = useMembers(me.permissions.includes('admin')).data?.items;
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<AccessGroupDTO | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: queryKeys.accessGroups() }), qc.invalidateQueries({ queryKey: queryKeys.members() })]);
  async function create() {
    try {
      await api('/api/v1/access-groups', { method: 'POST', json: { name: name.trim() } });
      setName('');
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  async function remove(g: AccessGroupDTO) {
    if (!window.confirm(`Delete “${g.name}”? Assets restricted only to it stay hidden from everyone except access managers.`)) return;
    await api(`/api/v1/access-groups/${g.id}`, { method: 'DELETE' }).catch((err) => toast(errorMessage(err), 'error'));
    await refresh();
  }
  return (
    <section className="panel-card">
      <h3 style={{ margin: 0 }}><Lock size={16} color="var(--accent-strong)" /> Access groups</h3>
      <p className="muted">A restricted asset is visible only to members of its groups and to people who manage access. Restrict an asset from its page.</p>
      <TableScroll>
      <table className="table compact" data-testid="access-groups">
        <thead><tr><th>Group</th><th>Members</th><th>Restricted assets</th><th /></tr></thead>
        <tbody>
          {(q.data?.items ?? []).map((g) => (
            <tr key={g.id}>
              <td className="wrap" style={{ fontWeight: 560 }}>{g.name}</td>
              <td className="muted wrap">{members ? g.members.map((id) => members.find((m) => m.id === id)?.displayName).filter(Boolean).join(', ') || '—' : g.memberCount}</td>
              <td className="mono nowrap">{g.assets}</td>
              <td className="actions">
                {members && <button className="btn ghost sm" onClick={() => setEditing(g)}>Members</button>}
                <button className="btn ghost icon sm" aria-label={`Delete ${g.name}`} onClick={() => void remove(g)}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input className="input" placeholder="New group, e.g. Legal hold" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && void create()} aria-label="New group name" />
        <button className="btn" disabled={!name.trim()} onClick={() => void create()} data-testid="create-group"><Plus size={14} /> Create</button>
      </div>
      {editing && members && <GroupMembersModal group={editing} members={members} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); }} />}
    </section>
  );
}

function GroupMembersModal({ group, members, onClose, onSaved }: { group: AccessGroupDTO; members: MemberDTO[]; onClose: () => void; onSaved: () => void }) {
  const [chosen, setChosen] = useState<string[]>(group.members);
  async function save() {
    try {
      await api(`/api/v1/access-groups/${group.id}/members`, { method: 'PUT', json: { userIds: chosen } });
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <Modal title={`Members of ${group.name}`} onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" onClick={() => void save()} data-testid="save-group-members">Save</button></>}>
      {members.filter((m) => !m.disabled).map((m) => (
        <label key={m.id} className="option-row">
          <input type="checkbox" checked={chosen.includes(m.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, m.id] : chosen.filter((x) => x !== m.id))} />
          {m.displayName} <span className="faint">· {m.roleName ?? m.role}</span>
        </label>
      ))}
    </Modal>
  );
}
